import { useMemo, useState } from 'react';

import type { TaskProps } from '~/types';

// --- Types ---

export type TaskSortOption = 'priority' | 'newest' | 'oldest';

export interface UseTaskFilteringReturn {
  filteredTasks: TaskProps[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setSortOption: (option: TaskSortOption) => void;
  shownCount: number;
  sortOption: TaskSortOption;
  totalCount: number;
}

// --- Sort ---

// Priority value mapping for sorting (HIGH > MEDIUM > LOW) — mirrors Board.tsx
const priorityValue: Record<string, number> = {
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

/**
 * Client-state hook that filters and sorts a task list for board display.
 *
 * Mirrors the shape of `useProjectFiltering` but is scoped to a single
 * project's tasks: search by title (case-insensitive) and sort by priority
 * or creation date. Draft tasks (`id` starting with `'temp-'`) are always
 * retained in the filtered list regardless of the search query, matching
 * `useProjectFiltering`'s treatment of `temp-` projects.
 *
 * Pure client-side derivation — no data fetching or mutations.
 */
export function useTaskFiltering(tasks: TaskProps[]): UseTaskFilteringReturn {
  const [searchQuery, setSearchQuery] = useState('');
  // Default to 'oldest' (createdAt ascending) to preserve task dependency
  // order — tasks are created in dependency order, so oldest tasks should
  // be implemented first.
  const [sortOption, setSortOption] = useState<TaskSortOption>('oldest');

  const filteredTasks = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const searched = !query
      ? tasks
      : tasks.filter(
          task => task.id.startsWith('temp-') || task.title.toLowerCase().includes(query),
        );

    return [...searched].sort((a, b) => {
      switch (sortOption) {
        case 'priority':
          return (priorityValue[b.priority] ?? 0) - (priorityValue[a.priority] ?? 0);
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        default:
          return 0;
      }
    });
  }, [tasks, searchQuery, sortOption]);

  return {
    filteredTasks,
    searchQuery,
    setSearchQuery,
    setSortOption,
    shownCount: filteredTasks.length,
    sortOption,
    totalCount: tasks.length,
  };
}
